// Standalone auto-init entry for the Livery connector, built as a CDN ES module:
//
//   <script type="module" src="https://…/leesto-connect.js" data-leesto-connect></script>
//
// Calls connectLeestoApp() on load so the embedding Livery shell can screenshot the
// page and record rrweb. No-op when the page isn't framed by Livery (connectLeestoApp
// bails when window.parent === window). rrweb + modern-screenshot stay in lazy
// chunks — fetched only on the first record/capture, so a normal page load of the
// host app downloads just this tiny entry.
//
// Self-hosted shell (replaces the built-in Leesto origins):
//   <script type="module" src="…/leesto-connect.js" data-leesto-connect
//           data-shell-origin="https://qa.acme.test"></script>
//
// Manual control (skip auto-init with data-leesto-manual):
//   <script type="module" src="…/leesto-connect.js" data-leesto-connect data-leesto-manual></script>
//   <script>window.LeestoConnect.connect({ highlightColor: "#6B8E23" })</script>

import { type ConnectLeestoAppOptions, connectLeestoApp } from "./app-client";

declare global {
  interface Window {
    LeestoConnect?: {
      /** Manually wire the connector (returns a disconnect fn). */
      connect: (options?: ConnectLeestoAppOptions) => () => void;
    };
  }
}

/** The connector's own <script> tag. In classic scripts this is
 *  document.currentScript; in module scripts that's null, so fall back to the
 *  element tagged with data-leesto-connect. */
function ownScript(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;
  return (
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-leesto-connect]")
  );
}

function optionsFromScript(el: HTMLScriptElement | null): ConnectLeestoAppOptions {
  const options: ConnectLeestoAppOptions = {};
  const highlightColor = el?.dataset.highlightColor;
  if (highlightColor) options.highlightColor = highlightColor;
  // Shell auto-hébergé : `data-shell-origin="https://a, https://b"` remplace les
  // origines Leesto par défaut. Sans cet attribut, seules celles-ci sont admises.
  const shellOrigins = (el?.dataset.shellOrigin ?? "")
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);
  if (shellOrigins.length > 0) options.shellOrigins = shellOrigins;
  return options;
}

if (typeof window !== "undefined") {
  window.LeestoConnect = { connect: (options) => connectLeestoApp(options) };
  const el = ownScript();
  // Auto-connect unless the host opted into manual init (data-leesto-manual).
  if (!el || el.dataset.leestoManual === undefined) {
    connectLeestoApp(optionsFromScript(el));
  }
}
